/**
 * Protected Route — Auth Gate for Route-Level Access Control
 *
 * Wraps route content and blocks rendering until the user is authenticated.
 * Optionally enforces a specific realm role (e.g., 'admin').
 *
 * ─── JS Parallel ───────────────────────────────────────────────────────────
 * This is the React equivalent of FastAPI's `Depends(get_current_user)`:
 * the backend returns 401 before the handler runs, while this component
 * renders a login prompt instead of `children`. Both are "fail early" gates.
 *
 * ─── Usage ─────────────────────────────────────────────────────────────────
 * Not wired to any route in F.1 — exported as scaffolding for F.2/F.3.
 *
 * Basic:
 *   <Route path="/settings" element={
 *     <ProtectedRoute><SettingsPage /></ProtectedRoute>
 *   } />
 *
 * Role-gated:
 *   <Route path="/admin" element={
 *     <ProtectedRoute requiredRole="admin"><AdminPage /></ProtectedRoute>
 *   } />
 */

import { type ReactNode } from "react";
import { LogIn, ShieldAlert } from "lucide-react";
import { useNavigate } from "react-router";
import { useAuth } from "../../providers/AuthProvider";

interface ProtectedRouteProps {
  children: ReactNode;
  /** Keycloak realm role required (e.g., 'admin'). Omit for any authenticated user. */
  requiredRole?: string;
}

// Demo-only: auth/backend decommissioned. Typed as `boolean` (not the literal
// `true`) so the auth-gating branches below stay reachable for the type-checker —
// they are preserved for when auth returns, just gated off.
const DEMO_ONLY: boolean = true;

export function ProtectedRoute({
  children,
  requiredRole,
}: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  // Demo-only: no backend auth exists. Render children directly so every demo
  // page is reachable without a login that no longer works.
  if (DEMO_ONLY) return <>{children}</>;

  // Loading: OIDC library checking sessionStorage for existing token
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm text-text-muted animate-pulse">Loading...</div>
      </div>
    );
  }

  // Unauthenticated: prompt to sign in
  if (!isAuthenticated || !user) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <LogIn className="w-8 h-8 text-text-muted" />
        <p className="text-sm text-text-muted">Sign in to access this page</p>
        <button
          type="button"
          onClick={() => navigate("/login")}
          className="px-4 py-2 text-sm font-medium rounded-md bg-accent-cyan/20 text-accent-cyan hover:bg-accent-cyan/30 transition-colors"
        >
          Sign in
        </button>
      </div>
    );
  }

  // Role check: user is authenticated but lacks required role
  if (requiredRole && !user.roles.includes(requiredRole)) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <ShieldAlert className="w-8 h-8 text-accent-yellow" />
        <p className="text-sm text-text-muted">
          This page requires the{" "}
          <span className="font-medium text-accent-yellow">{requiredRole}</span>{" "}
          role
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
